import React from 'react';
import Moment from 'moment/min/moment-with-locales.min';
import {FormattedMessage} from "react-intl";
import {Bold} from "../Components/Layout";
import {getSafely, isNonEmptyObject, isNonEmptyString} from "@tomeravni/easybizy-js-common/common";
import {GENDER_TYPE} from "./constants";

const SERVER_DATE_FORMAT = 'MM/DD/YYYY HH:mm';
const GENERIC_DATE_FORMAT = 'YYYY-MM-DDTHH:mm';
const DATE_KEY = 'YYMMDD';

const generateDefaultValues = (fields) => {
    return fields.reduce((acc, curr) => Object.assign(acc, {[curr.field]: curr.defaultValue || ''}), {});
};


const numberWithCommas = (x) => {
    const parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
};

const fixPageAnimationClass = (state) => state.initialized ? 'transition-appear-active' : 'transition-appear';

const utcNow = () => {
    const date = new Date();
    const now_utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
        date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());

    return new Date(now_utc);
};

const isExpired = (expiresOn) => {
    return expiresOn * 1000 - utcNow() < 0;
};


function isFunction(functionToCheck) {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

const SHEKEL_SIGN = '₪';

function humanizeDayOfWeek(date, appendDate) {
    const parsedDate = getParsedDate(date);
    const diffInDaysFromNow = parsedDate.startOf('day').diff(Moment().startOf('day'), 'days');
    if (diffInDaysFromNow === 0) {
        return <span><Bold><FormattedMessage id="app.today"/>!</Bold></span>
    } else if (diffInDaysFromNow === 1) {
        return <span><Bold><FormattedMessage id="app.tomorrow"/></Bold></span>
    } else {
        return <span><FormattedMessage
            id="app.day"/>&nbsp;{parsedDate.format('dddd')} {appendDate ? `(${serverDayDate(date)})` : ''}</span>
    }
}

function serverShortTime(date) {
    return getParsedDate(date).format('HH:mm')
}


function serverDayDate(date) {
    return getParsedDate(date).format('DD/MM')
}

function genericDate(date) {
    return getParsedDate(date).startOf('day').add(6, 'h').format(GENERIC_DATE_FORMAT)
}

function genericDateTime(date) {
    return getParsedDate(date).format(GENERIC_DATE_FORMAT)
}

function getParsedDate(stringDateOrMoment) {
    return Moment.isMoment(stringDateOrMoment) ? stringDateOrMoment : Moment(stringDateOrMoment, SERVER_DATE_FORMAT);
}

function getParsedDateAsKey(string) {
    return getParsedDate(string).format(DATE_KEY)
}

function toServerDate(string) {
    return Moment(string).format(SERVER_DATE_FORMAT);
}

function isSameDate(...dateStrings) {
    if (dateStrings.length <= 1) {
        return true;
    }

    const asDates = dateStrings.map(getParsedDate);
    return asDates.every(x => x.isSame(asDates[0], 'date'));
}

function lightenColor(color, percent) {
    const num = parseInt(color, 16),
        amt = Math.round(2.55 * percent),
        R = (num >> 16) + amt,
        B = (num >> 8 & 0x00FF) + amt,
        G = (num & 0x0000FF) + amt;

    return (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (B < 255 ? B < 1 ? 0 : B : 255) * 0x100 + (G < 255 ? G < 1 ? 0 : G : 255)).toString(16).slice(1);
}


function getSortedData(data, prop, desc) {
    return data.sort((a, b) => {
        let aValue = isFunction(prop) ? prop(a) : a[prop];
        let bValue = isFunction(prop) ? prop(b) : b[prop];
        return (aValue < bValue ? -1 : 1) * (desc ? -1 : 1)
    });
}

function resolveCustomerToken(config) {
    if (!isNonEmptyObject(config)) {
        return null;
    }

    return config.Token !== config.ContextOnlyToken ? config.Token : null;
}

const resolveContextNameAndCustomerToken = (config, match) => {
    const matchContext = getSafely(['params', 'context'], match);
    const matchCustomerToken = getSafely(['params', 'customer'], match);
    const configContext = getSafely(['ContextName'], config);
    const configCustomerToken = resolveCustomerToken(config);
    return {
        contextName: configContext || matchContext,
        customerToken: configCustomerToken || matchCustomerToken
    }
}

const resolvePath = (contextName, customerToken) => {
    if (!contextName) {
        return '';
    }

    let pathToReturn = `/${contextName}`

    if (isNonEmptyString(customerToken)) {
        pathToReturn += `/${customerToken}`
    }

    return pathToReturn;
}


const toInitials = (name) => {
    if (!isNonEmptyString(name)) {
        return 'UK';
    }

    const splitName = name.split(' ');
    if (splitName.length === 1) {
        return splitName[0].substr(0, 2).toUpperCase();
    }

    return `${getCapitalizedFirstLetter(getSafely([0], splitName))} ${getCapitalizedFirstLetter(getSafely([1], splitName))}`.trim();
}

const getCapitalizedFirstLetter = (str) => {
    if (!isNonEmptyString(str)) {
        return ''
    }

    return str[0].toUpperCase();
}

const isGenderMatch = (gender, employeeGender) => {
    return gender === GENDER_TYPE.MALE && employeeGender.toLowerCase() === 'male'
        || gender === GENDER_TYPE.FEMALE && employeeGender.toLowerCase() === 'female'
        || gender === GENDER_TYPE.ANY;
}

const adaptGenderToServer = (gender) => {
    switch (gender) {
        case GENDER_TYPE.MALE:
            return 'male';
        case GENDER_TYPE.FEMALE:
            return 'female';
        default:
            return 'nevermind';
    }
}

const asMutable = (obj) => {
    if (!obj) {
        return obj;
    }

    if (isFunction(obj.asMutable)) {
        return obj.asMutable({deep: true})
    }

    return {...obj};
}


export {
    generateDefaultValues,
    numberWithCommas,
    fixPageAnimationClass,
    utcNow,
    isExpired,
    isFunction,
    toInitials,
    SHEKEL_SIGN,
    humanizeDayOfWeek,
    serverShortTime,
    serverDayDate,
    getParsedDateAsKey,
    genericDate,
    genericDateTime,
    isSameDate,
    getSortedData,
    getParsedDate,
    toServerDate,
    lightenColor,
    resolveCustomerToken,
    resolvePath,
    resolveContextNameAndCustomerToken,
    isGenderMatch,
    adaptGenderToServer,
    asMutable
}
